import React, { useEffect, useState } from 'react';
import { API } from '../App';

export default function PlanSettingsModal({ plan, token, onPlanUpdated, onClose }) {
  const [title, setTitle] = useState(plan.title || '');
  const [keyInfo, setKeyInfo] = useState(null);
  const [newKey, setNewKey] = useState('');
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const headers = { Authorization: `Bearer ${token}` };
  const base = `${window.location.origin}/api/v1/plans/${plan.id}`;

  useEffect(() => {
    fetch(`${API}/api/plans/${plan.id}/api-key`, { headers: { Authorization: `Bearer ${token}` } })
      .then(async response => {
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Could not load API key status');
        setKeyInfo(data);
      })
      .catch(e => setError(e.message));
  }, [plan.id, token]);

  async function saveTitle(event) {
    event.preventDefault();
    const value = title.trim();
    if (!value || busy) return;
    setBusy(true); setError(''); setNotice('');
    try {
      const response = await fetch(`${API}/api/plans/${plan.id}`, {
        method: 'PUT', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: value, description: plan.description, color: plan.color }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Could not rename plan');
      onPlanUpdated(result);
      setNotice('Plan renamed.');
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function generateKey() {
    if (busy) return;
    setBusy(true); setError(''); setNotice('');
    try {
      const response = await fetch(`${API}/api/plans/${plan.id}/api-key`, { method: 'PUT', headers });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Could not create API key');
      setKeyInfo(result);
      setNewKey(result.key);
      setConfirmRotate(false);
      setNotice('Copy this key now. It will not be shown again.');
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function copyKey() {
    try { await navigator.clipboard.writeText(newKey); setNotice('API key copied.'); }
    catch { setError('Clipboard access failed. Select and copy the key manually.'); }
  }

  return <div onClick={onClose} style={overlay}>
    <div role="dialog" aria-modal="true" aria-label="Plan settings" onClick={e => e.stopPropagation()} style={modal}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
        <strong>Plan settings</strong><button onClick={onClose} aria-label="Close" style={button}>✕</button>
      </div>
      <form onSubmit={saveTitle}>
        <label htmlFor="plan-settings-title" style={label}>Plan name</label>
        <div style={{ display: 'flex', gap: 8 }}>
          <input id="plan-settings-title" value={title} maxLength={200} onChange={e => setTitle(e.target.value)} style={input} />
          <button disabled={busy || !title.trim() || title.trim() === plan.title} style={button}>Save</button>
        </div>
      </form>
      <section style={{ marginTop: 26, borderTop: '1px solid var(--border)', paddingTop: 20 }}>
        <strong style={{ fontSize: 14 }}>Read-only API key</strong>
        <p style={muted}>This key reads the executive summary, card outline, and individual cards in this plan. It cannot edit anything. Treat it like a password.</p>
        {keyInfo === null ? <p style={muted}>Loading key status…</p> : keyInfo.key_prefix ? <p style={muted}>Current key: <code>{keyInfo.key_prefix}…</code></p> : <p style={muted}>No API key has been created for this plan yet.</p>}
        {newKey && <>
          <label htmlFor="plan-api-key" style={label}>New key — shown once</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input id="plan-api-key" readOnly value={newKey} onFocus={e => e.target.select()} style={{ ...input, fontFamily: 'monospace', fontSize: 12 }} />
            <button type="button" onClick={copyKey} style={button}>Copy</button>
          </div>
        </>}
        {keyInfo && !keyInfo.key_prefix && <button type="button" disabled={busy} onClick={generateKey} style={button}>Create API key</button>}
        {keyInfo?.key_prefix && <div style={{ marginTop: 10 }}>
          {!confirmRotate ? <button type="button" disabled={busy} onClick={() => setConfirmRotate(true)} style={button}>Rotate key</button> : <>
            <p style={muted}>The current key will stop working immediately.</p>
            <button type="button" disabled={busy} onClick={generateKey} style={button}>Confirm rotation</button>{' '}
            <button type="button" onClick={() => setConfirmRotate(false)} style={button}>Cancel</button>
          </>}
        </div>}
        <p style={{ ...muted, marginTop: 20 }}>Send the key as <code>Authorization: Bearer YOUR_KEY</code>. The API returns JSON.</p>
        <ul style={{ ...muted, paddingLeft: 20, lineHeight: 1.7, overflowWrap: 'anywhere' }}>
          <li><code>GET {base}/summary</code></li>
          <li><code>GET {base}/outline</code></li>
          <li><code>GET {base}/cards/&#123;card_id&#125;</code></li>
        </ul>
      </section>
      {error && <p role="alert" style={{ color: '#ef4444', fontSize: 12 }}>{error}</p>}
      {notice && <p role="status" style={{ color: '#10b981', fontSize: 12 }}>{notice}</p>}
    </div>
  </div>;
}

const overlay = { position: 'fixed', inset: 0, zIndex: 120, background: 'rgba(0,0,0,.5)', display: 'grid', placeItems: 'center', padding: 16 };
const modal = { background: 'var(--bg-3)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: 16, padding: 24, width: 600, maxWidth: '100%', maxHeight: '85vh', overflowY: 'auto', boxShadow: 'var(--card-shadow-hover)' };
const button = { border: '1px solid var(--border)', borderRadius: 8, padding: '8px 11px', background: 'var(--bg-4)', color: 'var(--text-primary)', cursor: 'pointer', font: 'inherit', fontSize: 12, flexShrink: 0 };
const input = { flex: 1, minWidth: 0, padding: 9, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-4)', color: 'var(--text-primary)' };
const label = { display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 7 };
const muted = { fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 };
